import React from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import toast from "react-hot-toast";
import { EVENT_TAXONOMY_NAME_MAX_LENGTH, EventType } from "../../models/events";
import { yupConstants } from "../../utils/constants/yup";
import { getApiErrorMessage } from "../../utils/functions/api-errors";
import { useTenantScope } from "../../hooks/useTenantScope";
import {
  useCreateEventTypeMutation,
  useUpdateEventTypeMutation,
} from "../../store/services/events-types";

interface EventTypeFormProps {
  eventType?: EventType;
  handleClose: () => void;
}

interface EventTypeFormValues {
  name: string;
}

export const EventTypeForm = ({ eventType, handleClose }: EventTypeFormProps) => {
  const scope = useTenantScope();
  const tenantId = scope.tenantId;
  const [createEventType, { isLoading: isCreating }] = useCreateEventTypeMutation();
  const [updateEventType, { isLoading: isUpdating }] = useUpdateEventTypeMutation();
  const isSaving = isCreating || isUpdating;
  const isEdit = !!eventType;

  const { handleSubmit, touched, errors, values, handleBlur, handleChange } =
    useFormik<EventTypeFormValues>({
      initialValues: {
        name: eventType?.eventTypeName || "",
      },
      validationSchema: Yup.object({
        name: Yup.string()
          .trim()
          .required(yupConstants().required)
          .max(EVENT_TAXONOMY_NAME_MAX_LENGTH, yupConstants(EVENT_TAXONOMY_NAME_MAX_LENGTH).max),
      }),
      onSubmit: ({ name }) => {
        const trimmedName = name.trim();

        if (eventType) {
          updateEventType({
            id: eventType.eventTypeId,
            name: trimmedName,
            tenantId: tenantId ?? null,
          })
            .unwrap()
            .then(() => {
              toast.success("Tipo actualizado");
              handleClose();
            })
            .catch((error) => {
              toast.error(getApiErrorMessage(error) || "No se pudo actualizar el tipo");
            });
          return;
        }

        createEventType({ name: trimmedName, tenantId: tenantId ?? null })
          .unwrap()
          .then(() => {
            toast.success("Tipo creado");
            handleClose();
          })
          .catch((error) => {
            toast.error(getApiErrorMessage(error) || "No se pudo crear el tipo");
          });
      },
    });

  return (
    <form className="flex flex-column" style={{ gap: 18 }} onSubmit={handleSubmit}>
      <div className="flex flex-column" style={{ gap: 6 }}>
        <label htmlFor="eventTypeName" className="font-medium">
          Nombre
        </label>
        <InputText
          id="eventTypeName"
          name="name"
          className="w-full"
          value={values.name}
          onChange={handleChange}
          onBlur={handleBlur}
          invalid={!!touched.name && !!errors.name}
          disabled={isSaving}
        />
        {touched.name && errors.name && <small className="p-error">{errors.name}</small>}
      </div>

      <div className="form-actions flex flex-wrap justify-content-end pt-2" style={{ gap: "var(--space-18)" }}>
        <Button
          type="submit"
          label={isEdit ? "Guardar cambios" : "Crear tipo"}
          loading={isSaving}
        />
        <Button
          type="button"
          label="Cancelar"
          severity="secondary"
          outlined
          onClick={handleClose}
          disabled={isSaving}
        />
      </div>
    </form>
  );
};
